import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { useDaggerheartData } from '../context/DataContext';
import CookingPotAnimation from './animations/CookingPotAnimation';
import { refreshTokens } from '../services/SimpleTokenService';
import { generateRecipeWithAI, formatCreditsMessage, isAIGenerationAvailable } from '../services/aiRecipeService';
import { useAuth } from '../context/AuthContext';
import VitalAdjuster from './VitalAdjuster';
// Pattern-based implementation
import { FlavorCookingPattern } from './campaign-frames/patterns';
import { getFrameMechanicConfig } from '../config/campaignFrameConfig';

/**
 * Feature flag for pattern-based cooking.
 * Set to true to use the new config-driven FlavorCookingPattern.
 * Set to false to use the legacy implementation.
 */
const USE_PATTERN_COOKING = true;

/**
 * MakeFeastManager Component
 * Handles the "Make a Feast" downtime action for Beast Feast campaigns.
 * This is now a separate, focused action from harvesting.
 *
 * @param {object} props - The component props.
 * @param {object} props.character - The current character data object.
 * @param {function} props.updateCharacter - Function to update the character state.
 * @param {function} props.onManagerClose - Callback to close the modal.
 * @param {string} props.campaignId - The campaign ID for collaborative cooking (optional).
 * @param {boolean} props.isInCampaign - Whether the character is in a campaign.
 */
const MakeFeastManager = ({ character, updateCharacter, onManagerClose, campaignId, isInCampaign }) => {
  const allData = useDaggerheartData();
  const { user } = useAuth();

  // Use pattern-based implementation if feature flag is enabled
  if (USE_PATTERN_COOKING) {
    const frameStaticData = allData?.campaignFrames?.['beast-feast'] || null;
    const config = getFrameMechanicConfig('beast-feast', 'cooking');

    if (!frameStaticData || !config) {
      return <div>Loading Beast Feast data...</div>;
    }

    return (
      <FlavorCookingPattern
        frameId="beast-feast"
        mechanicId="cooking"
        config={config}
        character={character}
        updateCharacter={updateCharacter}
        allData={allData}
        frameStaticData={frameStaticData}
        onClose={onManagerClose}
        campaignId={campaignId}
        user={user}
      />
    );
  }

  // Legacy implementation below (when USE_PATTERN_COOKING is false)
  
  // Early return if data is not loaded
  if (!allData || !allData.campaignFrames || !allData.campaignFrames['beast-feast']) {
    return <div>Loading Beast Feast data...</div>;
  }

  // State for the cooking process
  const [selectedIngredients, setSelectedIngredients] = useState([]);
  const [dicePool, setDicePool] = useState([]);
  const [matchedSets, setMatchedSets] = useState([]);
  const [mealRating, setMealRating] = useState(0);
  const [cookingStep, setCookingStep] = useState('prepare'); // prepare, rolling, results
  const [recipeTokens, setRecipeTokens] = useState(0);

  // State for distributing the meal rating
  const [hpToHeal, setHpToHeal] = useState(0);
  const [stressToClear, setStressToClear] = useState(0);
  const [hopeToGain, setHopeToGain] = useState(0);
  const [scarsToClear, setScarsToClear] = useState(0);

  // State for cookbook
  const [recipeName, setRecipeName] = useState('');
  const [recipeDescription, setRecipeDescription] = useState('');
  const [showCookbook, setShowCookbook] = useState(false);

  // State for AI generation
  const [isGeneratingAI, setIsGeneratingAI] = useState(false);
  const [aiCreditsRemaining, setAICreditsRemaining] = useState(5); // Default to 5, will be updated

  // Animation state
  const [animationIngredients, setAnimationIngredients] = useState([]);
  const [animationResetTrigger, setAnimationResetTrigger] = useState(0);
  const [isStirring, setIsStirring] = useState(false);

  const availableIngredients = character.inventory?.ingredients || [];
  const cookbook = character.cookbook || [];
  
  // Debug log to see what ingredients we have
  console.log('MakeFeastManager - Available ingredients:', availableIngredients);
  
  // Debug: Log ingredients to see what we have
  console.log('Available ingredients in MakeFeastManager:', availableIngredients);

  // Helper function to get flavor color classes
  const getFlavorColor = (flavor) => {
    const colorMap = {
      'Sweet': 'bg-pink-100 text-pink-800',
      'Salty': 'bg-blue-100 text-blue-800',  
      'Bitter': 'bg-green-100 text-green-800',
      'Sour': 'bg-yellow-100 text-yellow-800',
      'Savory': 'bg-red-100 text-red-800',
      'Weird': 'bg-purple-100 text-purple-800'
    };
    return colorMap[flavor] || 'bg-gray-100 text-gray-800';
  };

  // Check if a recipe can be recreated with available ingredients
  const canRecreateRecipe = (recipe) => {
    if (!recipe.flavorTypes) return false;
    
    const requiredFlavors = recipe.flavorTypes;
    const availableFlavors = availableIngredients.map(ing => 
      ing.flavors?.map(f => f.profile) || []
    ).flat();
    
    return requiredFlavors.every(flavor => 
      availableFlavors.includes(flavor)
    );
  };

  // Recreate a recipe by selecting matching ingredients
  const recreateRecipe = (recipe) => {
    if (!recipe.flavorTypes) return;
    
    const requiredFlavors = [...recipe.flavorTypes];
    const selectedIds = [];
    
    // Find ingredients that match each required flavor
    requiredFlavors.forEach(flavor => {
      const matchingIng = availableIngredients.find(ing => 
        !selectedIds.includes(ing.id) && 
        ing.flavors?.some(f => f.profile === flavor)
      );
      if (matchingIng) {
        selectedIds.push(matchingIng.id);
      }
    });
    
    setSelectedIngredients(selectedIds);
    setRecipeName(recipe.name);
    setRecipeDescription(`Recreating: ${recipe.description}`);
  };

  // Check if this flavor profile matches a previous recipe
  const checkForRecipeMatch = (selectedIngs) => {
    const currentProfile = selectedIngs.map(ing => 
      ing.flavors.map(f => f.profile).sort().join('-')
    ).sort().join('|');
    
    const cookbook = character.cookbook || [];
    const matchingRecipe = cookbook.find(recipe => recipe.flavorProfile === currentProfile);
    
    if (matchingRecipe) {
      // Add tier bonus tokens (assuming tier 1 for now - could be made dynamic)
      const tierBonus = character.tier || 1;
      setRecipeTokens(tierBonus);
      return matchingRecipe;
    }
    
    return null;
  };

  const toggleIngredientSelection = (ingredientId) => {
    setSelectedIngredients(prev => {
      const newSelected = prev.includes(ingredientId)
        ? prev.filter(id => id !== ingredientId)
        : [...prev, ingredientId];
      
      // Update animation ingredients when selection changes
      if (!prev.includes(ingredientId)) {
        // Adding ingredient - trigger drop animation
        const ingredient = availableIngredients.find(ing => ing.id === ingredientId);
        if (ingredient) {
          setAnimationIngredients(current => [...current, ingredient]);
        }
      } else {
        // Removing ingredient - remove from animation
        setAnimationIngredients(current => 
          current.filter(ing => ing.id !== ingredientId)
        );
      }
      
      return newSelected;
    });
  };

  const beginCooking = () => {
    // Reset cooking state for a fresh start
    setDicePool([]);
    setMatchedSets([]);
    setMealRating(0);
    setHpToHeal(0);
    setStressToClear(0);
    setHopeToGain(0);
    setScarsToClear(0);
    
    // Start stirring immediately when cooking begins
    setIsStirring(true);
    
    const { flavorProfiles } = allData.campaignFrames['beast-feast'].customMechanics.cooking;
    let pool = [];
    const ingredientsToCook = availableIngredients.filter(ing => selectedIngredients.includes(ing.id));

    // Check for recipe match and add tokens
    const matchingRecipe = checkForRecipeMatch(ingredientsToCook);
    
    ingredientsToCook.forEach(ingredient => {
      ingredient.flavors.forEach(flavor => {
        const profile = flavorProfiles.find(p => p.name === flavor.profile);
        if (profile) {
          for (let i = 0; i < flavor.strength; i++) {
            pool.push({ 
              sides: parseInt(profile.die.slice(1)), 
              value: null, 
              id: `die-${pool.length}`,
              ingredientName: ingredient.name 
            });
          }
        }
      });
    });
    
    // Add recipe bonus tokens if this is a repeated recipe
    for (let i = 0; i < recipeTokens; i++) {
      pool.push({
        sides: 0, // Special token that can be discarded instead of a die
        value: 'TOKEN',
        id: `token-${i}`,
        ingredientName: matchingRecipe ? matchingRecipe.name : 'Recipe Bonus',
        isToken: true
      });
    }
    
    setDicePool(pool);
    setCookingStep('rolling');
  };

  // Smart dice removal algorithm to optimize matching chances
  const selectWorstDieForMatching = (rolledDice) => {
    // Group dice by their sides (die type)
    const diceGroups = rolledDice.reduce((groups, die) => {
      if (!groups[die.sides]) groups[die.sides] = [];
      groups[die.sides].push(die);
      return groups;
    }, {});

    // Priority 1: Remove dice from groups with only one die (can't possibly match)
    const singletonGroups = Object.values(diceGroups).filter(group => group.length === 1);
    if (singletonGroups.length > 0) {
      // Among singletons, prefer removing larger dice (lower match probability)
      const largestSingleton = singletonGroups.reduce((worst, group) => 
        group[0].sides > worst[0].sides ? group : worst
      );
      console.log(`🎯 Strategy: Removing singleton d${largestSingleton[0].sides} (can't match with any other die)`);
      return largestSingleton[0];
    }

    // Priority 2: From groups with multiple dice, remove the one with worst matching odds
    // This means removing dice with values that are least likely to be rolled again
    let worstDie = null;
    let worstMatchProbability = 1;

    for (const [sides, diceInGroup] of Object.entries(diceGroups)) {
      for (const die of diceInGroup) {
        // Calculate probability of matching this die's value
        // P(match) = (remaining dice of same type - 1) / sides
        const remainingOfSameType = diceInGroup.length - 1;
        const matchProbability = remainingOfSameType / parseInt(sides);
        
        if (matchProbability < worstMatchProbability) {
          worstMatchProbability = matchProbability;
          worstDie = die;
        }
      }
    }

    if (worstDie) {
      const matchChance = (worstMatchProbability * 100).toFixed(1);
      console.log(`🎯 Strategy: Removing d${worstDie.sides}=${worstDie.value} (${matchChance}% match chance - lowest odds)`);
    }

    // Fallback: if somehow no die was selected, return the first one
    return worstDie || rolledDice[0];
  };

  const rollDicePool = () => {
    // Start stirring animation when rolling dice
    setIsStirring(true);
    
    const newDicePool = dicePool.map(die => ({
      ...die, 
      value: Math.ceil(Math.random() * die.sides) 
    }));
   
    const counts = newDicePool.reduce((acc, die) => {
      acc[die.value] = (acc[die.value] || 0) + 1;
      return acc;
    }, {});

    const matches = Object.entries(counts).filter(([,count]) => count > 1).map(([value]) => parseInt(value));

    if (matches.length > 0) {
      // Found matches - collect them and continue with remaining dice
      const matchedDice = newDicePool.filter(die => matches.includes(die.value));
      const remainingDice = newDicePool.filter(die => !matches.includes(die.value));
      setMatchedSets(prev => [...prev, ...matchedDice]);
      setDicePool(remainingDice.map(d => ({...d, value: null}))); // Reset for next roll
    } else {
      // No matches found - use smart removal to optimize future matching chances
      const dieToRemove = selectWorstDieForMatching(newDicePool);
      const remainingDice = newDicePool.filter(die => die.id !== dieToRemove.id);
      
      console.log(`🎲 Auto-removed d${dieToRemove.sides} (rolled ${dieToRemove.value}) using optimal strategy`);
      console.log(`📋 Remaining dice: ${remainingDice.map(d => `d${d.sides}=${d.value}`).join(', ')}`);
      setDicePool(remainingDice.map(d => ({...d, value: null}))); // Reset for next roll
    }
  };

  useEffect(() => {
    if (cookingStep === 'rolling' && dicePool.length <= 1) {
      // Don't stop stirring yet - keep cooking until feast is applied
      
      const finalMatched = [...matchedSets, ...dicePool];
      const finalCounts = finalMatched.reduce((acc, die) => {
        acc[die.value] = (acc[die.value] || 0) + 1;
        return acc;
      }, {});
      const finalSets = Object.entries(finalCounts).filter(([,count]) => count > 1).map(([value]) => parseInt(value));
     
      const rating = finalSets.reduce((sum, val) => sum + val, 0);
      setMealRating(rating);
      setCookingStep('results');
    }
  }, [dicePool, matchedSets, cookingStep]);

  const applyFeastBenefits = () => {
    const totalSpent = hpToHeal + stressToClear + hopeToGain + scarsToClear;
    if (totalSpent > mealRating) {
      toast.success("Cannot spend more than the meal rating!");
      return;
    }

    const newHp = Math.min(character.maxHp, character.hp + hpToHeal);
    const newStress = Math.max(0, character.stress - stressToClear);
    const newHope = Math.min(6, character.hope + hopeToGain);
   
    // Handle scar clearing
    let newScars = [...(character.scars || [])];
    if (scarsToClear > 0) {
      // Remove the specified number of scars, starting with the highest Hope slot numbers
      newScars.sort((a, b) => (b.hopeSlot || 0) - (a.hopeSlot || 0));
      newScars = newScars.slice(scarsToClear);
    }
   
    const remainingIngredients = availableIngredients.filter(ing => !selectedIngredients.includes(ing.id));

    // Save recipe to cookbook if name and description provided
    let newCookbook = [...(character.cookbook || [])];
    if (recipeName.trim() && recipeDescription.trim()) {
      const usedIngredients = availableIngredients.filter(ing => selectedIngredients.includes(ing.id));

      // Generate flavor profile string for cache key matching
      const flavorProfileKey = usedIngredients.map(ing =>
        ing.flavors.map(f => {
          // Handle both string and object flavors
          if (typeof f === 'string') return f;
          if (f.profile) return f.profile;
          return '';
        }).filter(Boolean).sort().join('-')
      ).sort().join('|');

      // Extract actual flavor objects with strength for display
      const flavorObjects = [];
      const flavorMap = new Map(); // Track unique flavors and their max strength

      usedIngredients.forEach(ing => {
        ing.flavors.forEach(f => {
          const profile = typeof f === 'string' ? f : (f.profile || '');
          const strength = typeof f === 'string' ? 1 : (f.strength || 1);

          if (profile) {
            const currentStrength = flavorMap.get(profile) || 0;
            flavorMap.set(profile, Math.max(currentStrength, strength));
          }
        });
      });

      // Convert map to array of flavor objects
      flavorMap.forEach((strength, profile) => {
        flavorObjects.push({ profile, strength });
      });

      // Sort by strength descending, then alphabetically
      flavorObjects.sort((a, b) => {
        if (b.strength !== a.strength) return b.strength - a.strength;
        return a.profile.localeCompare(b.profile);
      });

      const recipeEntry = {
        id: `recipe-${Date.now()}`,
        name: recipeName.trim(),
        description: recipeDescription.trim(),
        ingredients: usedIngredients.map(ing => ({
          name: ing.name,
          source: ing.source,
          flavors: ing.flavors,
          ...(ing.isSpecial && ing.specialFeature && {
            isSpecial: true,
            specialFeature: ing.specialFeature
          })
        })),
        flavorProfile: flavorObjects, // Array of {profile, strength} objects for display
        flavorProfileKey: flavorProfileKey, // String for matching
        mealRating: mealRating,
        dateCreated: new Date().toLocaleDateString(),
        usedCount: 1
      };

      // Check if this recipe already exists (using the key)
      const existingRecipe = newCookbook.find(r => r.flavorProfileKey === flavorProfileKey);
      if (!existingRecipe) {
        newCookbook.push(recipeEntry);
      }
    }

    // Prepare updated character with feast results
    let updatedCharacter = {
      ...character,
      hp: newHp,
      stress: newStress,
      hope: newHope,
      scars: newScars,
      cookbook: newCookbook,
      inventory: {
        ...character.inventory,
        ingredients: remainingIngredients
      }
    };

    // Refresh rest-based abilities (Beast Feast counts as a long rest)
    const spellcastTrait = (character.subclass && allData?.subclasses?.[character.subclass]?.spellcastTrait) || null;
    updatedCharacter = refreshTokens(updatedCharacter, 'long-rest', spellcastTrait);

    updateCharacter(updatedCharacter);

    // Stop stirring when feast is completely finished
    setIsStirring(false);

    toast.success('🔄 Rest abilities refreshed!', { duration: 3000 });
    onManagerClose();
  };

  const resetCooking = () => {
    setSelectedIngredients([]);
    setDicePool([]);
    setMatchedSets([]);
    setMealRating(0);
    setCookingStep('prepare');
    setAnimationIngredients([]);
    setAnimationResetTrigger(prev => prev + 1);
    setIsStirring(false);
    setHpToHeal(0);
    setStressToClear(0);
    setHopeToGain(0);
    setScarsToClear(0);
  };

  // AI Recipe Generation Handler
  const handleAIGeneration = async () => {
    if (isGeneratingAI) return;

    setIsGeneratingAI(true);

    // Get used ingredients for AI prompt
    const usedIngredients = availableIngredients.filter(ing => selectedIngredients.includes(ing.id));

    // Generate recipe with AI
    const result = await generateRecipeWithAI({
      ingredients: usedIngredients,
      mealRating,
      campaignId: campaignId || `solo-${character.id}`, // Use character ID for solo mode
      userId: user?.uid || 'unknown'
    });

    if (result.success) {
      setRecipeName(result.name);
      setRecipeDescription(result.description);
      setAICreditsRemaining(result.creditsRemaining);

      // Show cache notification if recipe was cached
      if (result.cached) {
        toast.success('Recipe retrieved from your cookbook history!', { duration: 3000 });
      }
    } else {
      // Error handled by service, just update credits if available
      if (result.creditsRemaining !== null && result.creditsRemaining !== undefined) {
        setAICreditsRemaining(result.creditsRemaining);
      }
    }

    setIsGeneratingAI(false);
  };

  if (availableIngredients.length === 0) {
    return (
      <div className="space-y-6 text-center">
        <div>
          <h3 className="text-xl font-bold text-orange-600 mb-2">🍽️ Make a Feast</h3>
          <p className="text-gray-600 dark:text-gray-400">
            You need ingredients to prepare a feast
          </p>
        </div>

        <div className="bg-yellow-50 dark:bg-yellow-900/20 p-6 rounded-lg border border-yellow-200 dark:border-yellow-800">
          <p className="text-yellow-800 dark:text-yellow-200 mb-4">
            📦 No ingredients in inventory
          </p>
          <p className="text-sm text-yellow-700 dark:text-yellow-300">
            You'll need to harvest ingredients from defeated beasts before you can prepare a feast.
          </p>
        </div>

        <button 
          onClick={onManagerClose}
          className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600"
        >
          Close
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="text-center">
        <div className="flex items-center justify-center gap-3 mb-2">
          <h3 className="text-xl font-bold text-orange-600">🍽️ Make a Feast</h3>
          <button
            onClick={() => setShowCookbook(!showCookbook)}
            className="px-3 py-1 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors text-sm"
          >
            📖 {showCookbook ? 'Hide' : 'View'} Cookbook
          </button>
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Combine ingredients to cook a meal and recover during downtime
        </p>
      </div>

      {/* Cooking Animation - Always visible */}
      <div className="mb-4 flex justify-center">
        <CookingPotAnimation 
          ingredients={animationIngredients}
          resetTrigger={animationResetTrigger}
          isExternallyStirring={isStirring}
          externalCookingPhase={cookingStep === 'rolling' ? 'cooking' : 'prepare'}
          onCookingComplete={() => {
            console.log('Cooking animation completed!');
          }}
        />
      </div>

      {showCookbook && (
        <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
          <h4 className="font-semibold mb-3 text-blue-800 dark:text-blue-200">📖 Party Cookbook</h4>
          {cookbook.length === 0 ? (
            <p className="text-blue-700 dark:text-blue-300">No recipes recorded yet. Cook your first feast to start your cookbook!</p>
          ) : (
            <div className="space-y-3">
              {cookbook.map((recipe, index) => (
                <div key={index} className="bg-white dark:bg-gray-700 p-3 rounded-lg border">
                  <div className="flex justify-between items-start mb-2">
                    <h5 className="font-medium">{recipe.name}</h5>
                    <div className="flex items-center gap-2">
                      <div className="flex gap-1">
                        {(recipe.flavorTypes || recipe.flavors)?.map((flavor, idx) => (
                          <span key={idx} className={`px-2 py-1 rounded text-xs ${getFlavorColor(flavor)}`}>
                            {flavor}
                          </span>
                        ))}
                      </div>
                      {canRecreateRecipe(recipe) && (
                        <button
                          onClick={() => recreateRecipe(recipe)}
                          className="px-2 py-1 bg-green-500 text-white text-xs rounded hover:bg-green-600"
                          title="Recreate this recipe with available ingredients"
                        >
                          🍳 Recreate
                        </button>
                      )}
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">{recipe.description}</p>
                  
                  {/* Show ingredient sources if available */}
                  {recipe.ingredients && Array.isArray(recipe.ingredients) && recipe.ingredients[0]?.source && (
                    <div className="mb-2">
                      <p className="text-xs text-gray-500 mb-1">Original ingredients:</p>
                      <div className="flex flex-wrap gap-1">
                        {recipe.ingredients.map((ing, idx) => (
                          <span key={idx} className="text-xs bg-gray-100 dark:bg-gray-600 px-2 py-1 rounded">
                            {typeof ing === 'string' ? ing : `${ing.name} (${ing.source})`}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  <p className="text-xs text-gray-500">
                    First made: {recipe.dateCreated} | Rating: {recipe.mealRating} | Used {recipe.usedCount || 1} times
                    {!canRecreateRecipe(recipe) && <span className="text-red-500 ml-2">Missing ingredients</span>}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {cookingStep === 'prepare' && (
        <div className="space-y-4">
          <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
            <h4 className="font-semibold mb-3">
              Select Ingredients ({availableIngredients.length} available)
            </h4>
            <div className="max-h-48 overflow-y-auto space-y-2 border rounded-lg p-2 bg-white dark:bg-gray-700">
              {availableIngredients.map(ing => (
                <label key={ing.id} className="flex items-center p-3 bg-gray-50 dark:bg-gray-600/50 rounded-md cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600">
                  <input 
                    type="checkbox" 
                    checked={selectedIngredients.includes(ing.id)} 
                    onChange={() => toggleIngredientSelection(ing.id)} 
                    className="h-4 w-4 rounded text-orange-600 focus:ring-orange-500"
                  />
                  <div className="ml-3 flex-1">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">{ing.name}</span>
                        {ing.isSpecial && (
                          <span className="text-xs bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300 px-2 py-1 rounded font-medium">
                            ⭐ Special
                          </span>
                        )}
                      </div>
                      {ing.source && (
                        <span className="text-xs text-blue-600 dark:text-blue-400 bg-blue-100 dark:bg-blue-900/30 px-2 py-1 rounded">
                          {ing.source}
                        </span>
                      )}
                    </div>
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      Flavors: {ing.flavors?.map(f => `${f.profile} (${f.strength})`).join(', ') || 'Unknown'}
                    </span>
                    {ing.isSpecial && ing.specialFeature && (
                      <div className="text-xs text-yellow-700 dark:text-yellow-300 mt-1 p-1 bg-yellow-50 dark:bg-yellow-900/20 rounded">
                        <strong>{ing.specialFeature.name}:</strong> {ing.specialFeature.description}
                      </div>
                    )}
                  </div>
                </label>
              ))}
            </div>
          </div>

          {selectedIngredients.length > 0 && (
            <div className="bg-orange-50 dark:bg-orange-900/20 p-4 rounded-lg border border-orange-200 dark:border-orange-800">
              <p className="text-orange-800 dark:text-orange-200 text-sm font-medium mb-2">
                Selected: {selectedIngredients.length} ingredient(s)
              </p>
              
              {/* Recipe match indicator */}
              {(() => {
                const selectedIngs = availableIngredients.filter(ing => selectedIngredients.includes(ing.id));
                const matchingRecipe = checkForRecipeMatch(selectedIngs);
                return matchingRecipe && (
                  <div className="mt-2 p-2 bg-green-100 dark:bg-green-900/30 rounded border border-green-300 dark:border-green-600">
                    <p className="text-green-800 dark:text-green-200 text-xs font-medium">
                      📖 Recipe Match: "{matchingRecipe.name}"
                    </p>
                    <p className="text-green-700 dark:text-green-300 text-xs">
                      +{character.tier || 1} bonus tokens from cookbook experience
                    </p>
                  </div>
                );
              })()}
            </div>
          )}

          <div className="flex space-x-2">
            <button 
              onClick={beginCooking} 
              disabled={selectedIngredients.length === 0} 
              className="flex-1 py-3 bg-orange-600 text-white rounded-md hover:bg-orange-700 disabled:bg-gray-400 disabled:cursor-not-allowed font-medium"
            >
              Start Cooking
            </button>
            <button 
              onClick={onManagerClose}
              className="px-6 py-3 bg-gray-500 text-white rounded-md hover:bg-gray-600"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {cookingStep === 'rolling' && (
        <div className="space-y-4">
          <div className="text-center">
            <h4 className="text-lg font-semibold mb-2">🎲 Cooking Process</h4>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Roll dice and collect matching sets. Click dice to discard them.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
              <h4 className="font-semibold mb-3">Dice Pool ({dicePool.length})</h4>
              <div className="flex flex-wrap justify-center gap-2 p-4 bg-white dark:bg-gray-700 rounded-lg min-h-[6rem]">
                {dicePool.map(die => (
                  <div 
                    key={die.id} 
                    className={`p-2 w-12 h-12 flex items-center justify-center font-bold text-lg rounded-md shadow ${
                      die.isToken 
                        ? 'bg-yellow-200 dark:bg-yellow-800 text-yellow-800 dark:text-yellow-200 border-2 border-yellow-400'
                        : 'bg-gray-100 dark:bg-gray-600'
                    }`}
                    title={die.isToken ? `Recipe bonus token` : die.value ? `Rolled ${die.value} (from ${die.ingredientName})` : `d${die.sides}`}
                  >
                    {die.isToken ? '🎫' : die.value ? die.value : `d${die.sides}`}
                  </div>
                ))}
                {dicePool.length > 1 && dicePool.every(die => die.value !== null) && (
                  <div className="w-full text-center text-sm text-gray-600 dark:text-gray-400 mt-2">
                    🎯 Smart removal will automatically optimize your dice pool
                  </div>
                )}
              </div>
            </div>

            <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
              <h4 className="font-semibold mb-3 text-green-800 dark:text-green-200">
                Matched Sets ({matchedSets.length})
              </h4>
              <div className="flex flex-wrap justify-center gap-2 p-4 bg-white dark:bg-gray-700 rounded-lg min-h-[6rem]">
                {matchedSets.map(die => (
                  <div 
                    key={die.id} 
                    className="p-2 w-12 h-12 flex items-center justify-center font-bold text-lg bg-green-100 dark:bg-green-800 text-green-800 dark:text-green-100 rounded-md shadow"
                    title={`From ${die.ingredientName}`}
                  >
                    {die.value}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="text-center">
            <button 
              onClick={rollDicePool} 
              className="px-8 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium"
            >
              Roll Dice
            </button>
          </div>
        </div>
      )}

      {cookingStep === 'results' && (
        <div className="space-y-6">
          <div className="text-center bg-green-50 dark:bg-green-900/20 p-6 rounded-lg border border-green-200 dark:border-green-800">
            <h3 className="text-2xl font-bold text-green-600 dark:text-green-400 mb-2">
              🎉 Feast Complete!
            </h3>
            <p className="text-4xl font-bold text-green-700 dark:text-green-300 mb-2">
              {mealRating}
            </p>
            <p className="text-sm text-green-600 dark:text-green-400">
              Meal Rating Points to Distribute
            </p>
          </div>

          <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
            <h4 className="font-semibold mb-4">Distribute Benefits</h4>

            {/* Meal Rating Bar */}
            <div className="mb-6 p-4 bg-gray-900 rounded-lg border border-green-600">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-300">Meal Rating Available</span>
                <span className="text-2xl font-bold text-green-400">
                  {mealRating - (hpToHeal + stressToClear + hopeToGain + scarsToClear)} / {mealRating}
                </span>
              </div>
              <div className="w-full bg-gray-700 rounded-full h-3">
                <div
                  className="bg-gradient-to-r from-green-500 to-green-600 h-3 rounded-full transition-all duration-300"
                  style={{
                    width: `${Math.max(0, Math.min(100, ((mealRating - (hpToHeal + stressToClear + hopeToGain + scarsToClear)) / mealRating) * 100))}%`
                  }}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <VitalAdjuster
                label="Heal HP"
                vitalType="hp"
                currentValue={character.hp || 0}
                maxValue={character.maxHp || 8}
                adjustmentValue={hpToHeal}
                onAdjustmentChange={(delta) => {
                  const remainingBudget = mealRating - (stressToClear + hopeToGain + scarsToClear);
                  const maxAllowed = Math.min(remainingBudget, (character.maxHp || 8) - (character.hp || 0));
                  const newValue = Math.max(0, Math.min(maxAllowed, hpToHeal + delta));
                  setHpToHeal(newValue);
                }}
                remainingBudget={mealRating - (stressToClear + hopeToGain + scarsToClear)}
              />

              <VitalAdjuster
                label="Clear Stress"
                vitalType="stress"
                currentValue={character.stress || 0}
                maxValue={character.maxStress || 5}
                adjustmentValue={stressToClear}
                onAdjustmentChange={(delta) => {
                  const remainingBudget = mealRating - (hpToHeal + hopeToGain + scarsToClear);
                  const maxAllowed = Math.min(remainingBudget, character.stress || 0);
                  const newValue = Math.max(0, Math.min(maxAllowed, stressToClear + delta));
                  setStressToClear(newValue);
                }}
                remainingBudget={mealRating - (hpToHeal + hopeToGain + scarsToClear)}
              />

              <VitalAdjuster
                label="Gain Hope"
                vitalType="hope"
                currentValue={character.hope || 0}
                maxValue={6}
                adjustmentValue={hopeToGain}
                onAdjustmentChange={(delta) => {
                  const remainingBudget = mealRating - (hpToHeal + stressToClear + scarsToClear);
                  const maxAllowed = Math.min(remainingBudget, 6 - (character.hope || 0));
                  const newValue = Math.max(0, Math.min(maxAllowed, hopeToGain + delta));
                  setHopeToGain(newValue);
                }}
                remainingBudget={mealRating - (hpToHeal + stressToClear + scarsToClear)}
              />

              {/* Keep scars input simple for now */}
              <div className="bg-gray-900 p-4 rounded-lg border border-gray-700">
                <label className="block text-sm font-medium mb-2 text-gray-300">Clear Scars</label>
                <input
                  type="number"
                  value={scarsToClear}
                  onChange={e => {
                    const remainingBudget = mealRating - (hpToHeal + stressToClear + hopeToGain);
                    const maxAllowed = Math.min(remainingBudget, (character.scars || []).length);
                    setScarsToClear(Math.max(0, Math.min(maxAllowed, parseInt(e.target.value) || 0)));
                  }}
                  min="0"
                  max={Math.min(mealRating - (hpToHeal + stressToClear + hopeToGain), (character.scars || []).length)}
                  className="w-full p-3 rounded-md bg-gray-700 border border-gray-600 text-gray-100 focus:ring-2 focus:ring-green-500"
                />
                <p className="text-xs text-gray-400 mt-2">
                  Current: {(character.scars || []).length} scars
                </p>
              </div>
            </div>

            <div className="mt-4 p-3 bg-gray-900 dark:bg-gray-700 rounded-lg border border-gray-600">
              <p className="text-sm font-bold text-center">
                Total Spent: <span className={hpToHeal + stressToClear + hopeToGain + scarsToClear > mealRating ? 'text-red-400' : 'text-green-400'}>
                  {hpToHeal + stressToClear + hopeToGain + scarsToClear}
                </span> / {mealRating}
              </p>
              {hpToHeal + stressToClear + hopeToGain + scarsToClear > mealRating && (
                <p className="text-xs text-red-600 text-center mt-1">
                  Cannot spend more than your meal rating!
                </p>
              )}
            </div>
          </div>

          {/* Cookbook Entry */}
          <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
            <div className="flex justify-between items-center mb-3">
              <h4 className="font-semibold text-blue-800 dark:text-blue-200">📖 Add to Cookbook</h4>
            </div>
            <p className="text-sm text-blue-700 dark:text-blue-300 mb-3">
              Record this recipe in your party's cookbook for future bonuses when making the same flavor combination!
            </p>

            <div className="space-y-3">
              <div>
                <div className="flex gap-2 items-end mb-1">
                  <label className="block text-sm font-medium flex-1">Recipe Name</label>
                  <button
                    onClick={handleAIGeneration}
                    disabled={!isAIGenerationAvailable(aiCreditsRemaining, isGeneratingAI)}
                    className="px-3 py-1 bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs font-medium rounded-md hover:from-purple-600 hover:to-pink-600 disabled:from-gray-400 disabled:to-gray-400 disabled:cursor-not-allowed transition-all flex items-center gap-1"
                    title="Discover a recipe"
                  >
                    {isGeneratingAI ? (
                      <>
                        <svg className="animate-spin h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Discovering...
                      </>
                    ) : (
                      <>
                        ✨ Discover Recipe
                      </>
                    )}
                  </button>
                </div>
                <input
                  type="text"
                  value={recipeName}
                  onChange={e => setRecipeName(e.target.value)}
                  className="w-full p-2 rounded-md bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., Hearty Beast Stew"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Recipe Description</label>
                <textarea
                  value={recipeDescription}
                  onChange={e => setRecipeDescription(e.target.value)}
                  className="w-full p-2 rounded-md bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-blue-500"
                  placeholder="Describe the cooking method and how it turned out..."
                  rows="3"
                />
              </div>
            </div>
          </div>

          <div className="flex space-x-2">
            <button 
              onClick={applyFeastBenefits} 
              disabled={hpToHeal + stressToClear + hopeToGain + scarsToClear > mealRating}
              className="flex-1 py-3 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed font-medium"
            >
              Finish Feast
            </button>
            <button 
              onClick={resetCooking}
              className="px-6 py-3 bg-orange-600 text-white rounded-md hover:bg-orange-700"
            >
              Cook Again
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MakeFeastManager;
